import React, { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import styled from 'styled-components'

import { CloseButton, LinkButton, PrimaryButton } from '../buttons/Buttons'
import { palette, fonts, shadows, mediaQueries } from '../../styles/variables'
import { translate } from '../../utils/translation'
import { Text } from '../topography'
import Icon from '../icon/Icon'
import checkIconAccents01 from '../../styles/assets/check_light_accents01.svg'
import starIconYellow from '../../styles/assets/star_yellow.svg'
import { formatNumber } from '../../utils/numbers'
import {
  getAddonByKey,
  getAddonPrice,
  getCurrencyAndRegion,
} from '../../utils/payment'
import { purchaseAddons } from '../../utils/api'
import { Mixpanel } from '../../utils/mixpanel'
import { allAddonsTypes } from '../../utils/addonTypes'

import Modal, { ModalContent } from './Modal'

const desktopStyle = {
  width: '95%',
  maxWidth: '1308px',
  height: 'fit-content',
  maxHeight: '95vh',
  overflow: 'unset',
  overflowY: 'auto',
  borderRadius: 0,
}

const mobileStyle = {}

const ModalContentWrapper = styled(ModalContent)({
  height: 'fit-content',
  position: 'relative',
  padding: '40px 80px',
  [mediaQueries.smallDesktop]: {
    padding: '40px 50px',
  },
  [mediaQueries.laptop]: {
    padding: '40px 30px',
  },
  [mediaQueries.mobile]: {
    padding: '40px 20px',
  },
})

const HeaderWrapper = styled.div({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '50px',
  [mediaQueries.laptop]: {
    flexDirection: 'column',
    gap: '16px',
  },
})

const HeaderLeft = styled.div({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  gap: '8px',
  [mediaQueries.laptop]: {
    alignItems: 'center',
  },
})

const HeaderRight = styled.div({
  backgroundColor: palette.white,
  boxShadow: shadows.shadowH,
  padding: '24px',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  gap: '8px',
  [mediaQueries.mobile]: {
    padding: '12px',
  },
})

const PlansWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '40px 0',
  gap: '50px',
  [mediaQueries.smallDesktop]: {
    gap: '35px',
  },
  [mediaQueries.laptop]: {
    gap: '20px',
  },
  [mediaQueries.mobile]: {
    margin: '24px 0 40px',
    flexDirection: 'column',
    gap: '24px',
  },
})

const Border = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '4px',
  background: palette.orange,
})

const PlanCard = styled.div((props) => ({
  position: 'relative',
  flex: 1,
  borderRadius: '2px',
  background: palette.white,
  boxShadow: shadows.shadowH,
  padding: '40px',
  overflow: 'hidden',
  height: '100%',
  [mediaQueries.laptop]: {
    padding: '40px 20px',
  },
  [mediaQueries.mobile]: {
    width: '100%',
    maxWidth: '288px',
    padding: '32px 20px',
    order: props.order === 0 ? 2 : props.order,
  },
}))

const PlanUl = styled.ul({
  padding: 0,
  margin: '24px 0 40px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  [mediaQueries.mobile]: {
    margin: '24px 0',
  },
})

const PlanLi = styled.li({
  listStyleType: 'none',
  textDecoration: 'none',
  fontFamily: fonts.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '16px',
})

const PopularBadge = styled.div(() => {
  return {
    position: 'absolute',
    top: '22px',
    right: '-37px',
    width: '167px',
    height: '40px',
    background: palette.white,
    transform: 'rotate(43deg)',
    boxShadow: shadows.shadowI,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }
})

const FooterWrapper = styled.div({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
})

const AIW_PLANS = [
  allAddonsTypes.ADDON_AIW_BUSINESS,
  allAddonsTypes.ADDON_AIW_BLOGGER,
  allAddonsTypes.ADDON_AIW_SHAKESPEARE,
]

export default ({ isOpen, user, onAccept, onDeny, ...rest }) => {
  const [addons, setAddons] = useState(null)
  const [isProcessing, setIsProcessing] = useState('')

  const desktopPlanCards = {
    [allAddonsTypes.ADDON_AIW_BLOGGER]: {
      title: translate('aiw_plan_blogger'),
      benefits: [
        translate('aiw_plan_n_words', formatNumber(25000)),
        translate('aiw_plan_n_articles', formatNumber(15)),
        translate('aiw_plan_n_limits'),
        translate('aiw_plan_n_features'),
      ],
    },
    [allAddonsTypes.ADDON_AIW_BUSINESS]: {
      title: translate('aiw_plan_business'),
      benefits: [
        translate('aiw_plan_n_words', formatNumber(100000)),
        translate('aiw_plan_n_articles', formatNumber(60)),
        translate('aiw_plan_n_limits'),
        translate('aiw_plan_n_features'),
      ],
    },
    [allAddonsTypes.ADDON_AIW_SHAKESPEARE]: {
      title: translate('aiw_plan_shakespeare'),
      benefits: [
        translate('aiw_plan_n_words', formatNumber(500000)),
        translate('aiw_plan_n_articles', formatNumber(300)),
        translate('aiw_plan_n_limits'),
        translate('aiw_plan_n_features'),
      ],
    },
  }

  useEffect(() => {
    if (isOpen) {
      Mixpanel.trackAiwOrderBumpView()
      setIsProcessing('')
    }
  }, [isOpen])

  useEffect(() => {
    if (!isEmpty(user.addons)) {
      const { region, currency, planCode } = getCurrencyAndRegion(
        user.countryCode,
      )

      const newAddons = {}

      AIW_PLANS.forEach((plan) => {
        const addonDetail = getAddonByKey(user.addons, plan)
        const addonPrice = addonDetail
          ? getAddonPrice(addonDetail, currency, planCode)
          : 0

        newAddons[plan] = {
          code: addonDetail.code,
          price: translate(
            'amount_per_month',
            translate(
              'currency_at_symbol',
              formatNumber(addonPrice, true, 0, region, false),
              '',
            ),
          ),
        }
      })

      setAddons(newAddons)
    }
  }, [user.addons, user.countryCode])

  if (isEmpty(addons)) return null

  return (
    <Modal
      isOpen={isOpen}
      desktopStyle={desktopStyle}
      mobileStyle={mobileStyle}
      overlayStyle={{ zIndex: 999999 }}
      {...rest}
    >
      <ModalContentWrapper>
        {!isProcessing && (
          <CloseButton onClick={onDeny} styles={{ top: '20px' }} />
        )}
        <HeaderWrapper>
          <HeaderLeft>
            <Text
              fontFamily={fonts.primaryMedium}
              fontSize="48px"
              mobileFontSize="32px"
              lineHeight="59px"
              mobileLineHeight="48px"
              color={palette.accents02}
              whiteSpace="inherit"
              mobileTextAlign="center"
            >
              {translate('start_ai_writing')}
            </Text>
            <Text
              fontSize="18px"
              mobileFontSize="16px"
              lineHeight="24px"
              fontFamily={fonts.primaryMedium}
              mobileTextAlign="center"
            >
              {translate('aiw_offer_subheading')}
            </Text>
          </HeaderLeft>
          <HeaderRight>
            <Icon src={starIconYellow} size="40px" />
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                gap: '4px',
              }}
            >
              <Text
                fontSize="22px"
                lineHeight="28px"
                fontFamily={fonts.primaryMedium}
              >
                {translate('your_current_plan')}
              </Text>
              <Text fontSize="16px" lineHeight="28px">
                {translate('basic_credits')}
              </Text>
            </div>
          </HeaderRight>
        </HeaderWrapper>
        <PlansWrapper>
          {AIW_PLANS.map((plan, index) => (
            <PlanCard key={plan} order={index}>
              <Border />
              {plan === allAddonsTypes.ADDON_AIW_BLOGGER && (
                <PopularBadge>
                  <Text
                    color={palette.blue}
                    fontSize="16px"
                    lineHeight="1em"
                    fontFamily={fonts.primaryMedium}
                    textAlign="center"
                    style={{ maxWidth: '70%' }}
                  >
                    {translate('popular_badge')}
                  </Text>
                </PopularBadge>
              )}

              <Text
                fontSize="32px"
                lineHeight="32px"
                fontFamily={fonts.primaryMedium}
                color={palette.orange}
                textAlign="center"
              >
                {desktopPlanCards[plan].title}
              </Text>
              <Text
                fontSize="20px"
                lineHeight="20px"
                boldFontSize="48px"
                textAlign="center"
                margin="40px 0 10px"
                dangerouslySetInnerHTML={{
                  __html: translate(addons[plan].price),
                }}
              />
              <PlanUl>
                {desktopPlanCards[plan].benefits.map(
                  (benefit, benefitsIndex) => (
                    <PlanLi key={`benefit_${benefitsIndex}`}>
                      <Icon src={checkIconAccents01} size="16px" />
                      <Text
                        fontSize="18px"
                        lineHeight="28px"
                        boldColor={palette.darkGray}
                        dangerouslySetInnerHTML={{
                          __html: benefit,
                        }}
                      />
                    </PlanLi>
                  ),
                )}
              </PlanUl>
              <PrimaryButton
                tag={translate('aiw_offer_yes')}
                theme="orange"
                style={{
                  width: '100%',
                  color: palette.white,
                  backgroundColor: isProcessing ? palette.gray : palette.blue,
                  border: `1px solid ${
                    isProcessing ? palette.gray : palette.blue
                  }`,
                  height: '50px',
                  fontSize: '14px',
                  lineHeight: '16px',
                  letterSpacing: '1.12px',
                }}
                disabled={isProcessing}
                isLoading={isProcessing === plan}
                onClick={() => {
                  setIsProcessing(plan)
                  purchaseAddons(addons[plan].code)
                    .then(() => {
                      Mixpanel.trackAiwOrderBumpSuccess(addons[plan], user)
                      onAccept(plan)
                    })
                    .catch((error) => {
                      console.error('error', error)
                      onAccept(plan, 'failed')
                    })
                }}
              />
            </PlanCard>
          ))}
        </PlansWrapper>
        <FooterWrapper>
          <LinkButton
            style={{
              textDecoration: 'underline',
              fontFamily: fonts.primaryMedium,
              color: isProcessing ? palette.gray : palette.darkGray,
            }}
            disabled={isProcessing}
            onClick={onDeny}
          >
            {translate('aiw_offer_no')}
          </LinkButton>
        </FooterWrapper>
      </ModalContentWrapper>
    </Modal>
  )
}
